import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChatMessage, GenerationParams, Platform, Brand, ThumbnailConcept, TextObject, TextStyleTemplate, Variation } from '../types';
import { ICONS, HEBREW_FONTS, DEFAULT_TEXT_STYLES } from '../constants.tsx';
import { getProjectIdeas, markSavedIdeaAsUsed, SavedIdea, saveIdeas, getProjectChatMessages, saveChatMessage, uploadChatImage, updateChatMessageImageUrl, generateThumbnailImageCallable, generateConceptsCallable, editThumbnailImageCallable, adaptThumbnailToPlatformCallable } from '../services/firebase';
import CanvasEditor from './CanvasEditor';
import NotEnoughCreditsModal from './NotEnoughCreditsModal';
import { useCredits } from '../contexts';
import { reportError } from '../services/errorReporting';

interface Props {
  variations: Variation[];
  activeVariationIndex: number;
  onVariationIndexChange: (index: number) => void;
  onNewVariation: (image: string, currentTexts: TextObject[], concept?: ThumbnailConcept) => void;
  onBackfillVariationConcept?: (variationIndex: number, concept: ThumbnailConcept) => void;
  params: GenerationParams;
  brand: Brand | null;
  concept?: ThumbnailConcept;
  concepts?: ThumbnailConcept[]; // New prop for available concepts
  onBack: () => void;
  onAutoSave: (variationIndex: number, texts: TextObject[]) => void;
  isSaving?: boolean;
  userId?: string | null;
  brandId?: string | null;
  projectId?: string | null;
  /** Ref image from landing handoff (data URL or Storage URL); used for first thumbnail generation. */
  handoffReferenceImageUrl?: string | null;
}

type Tab = 'background' | 'text' | 'concepts';

const Editor: React.FC<Props> = ({ variations, activeVariationIndex, onVariationIndexChange, onNewVariation, onBackfillVariationConcept, params, brand, concept, concepts, onBack, onAutoSave, isSaving, userId, brandId, projectId, handoffReferenceImageUrl }) => {
  // Credits
  const { hasEnoughCredits, deductCredits, refreshCredits, CREDIT_COSTS } = useCredits();
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsNeeded, setCreditsNeeded] = useState(CREDIT_COSTS.IMAGE_GENERATION);

  // Get current variation data
  const currentVariation = variations[activeVariationIndex];
  const currentImage = currentVariation?.imageUrl || '';
  
  const [activeTab, setActiveTab] = useState<Tab>(variations.length === 0 ? 'concepts' : 'background');
  
  // Local params state to handle changes within the editor (e.g. platform adaptation)
  const [localParams, setLocalParams] = useState<GenerationParams>(params);

  // --- Concepts State ---
  const [localConcepts, setLocalConcepts] = useState<ThumbnailConcept[]>(concepts || []);
  const [savedIdeas, setSavedIdeas] = useState<SavedIdea[]>([]);
  const [executedIds, setExecutedIds] = useState<Set<string>>(new Set());
  const [isRegenerating, setIsRegenerating] = useState(false);
  const prevVariationsLengthRef = useRef(variations.length);
  // Edit hook (mainTitle) popover: which concept is being edited and draft value
  const [editingConcept, setEditingConcept] = useState<ThumbnailConcept | null>(null);
  const [editMainTitleDraft, setEditMainTitleDraft] = useState('');

  // --- Text State - initialized from current variation's texts ---
  const [texts, setTexts] = useState<TextObject[]>(currentVariation?.texts || []);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [isEditingText, setIsEditingText] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Track which variation's texts we're currently editing
  const [lastLoadedVariationIndex, setLastLoadedVariationIndex] = useState(activeVariationIndex);
  
  // Store actual dimensions to allow smart text placement
  const [imageDimensions, setImageDimensions] = useState<{width: number, height: number} | null>(null);

  // --- Styles State ---
  const [savedStyles, setSavedStyles] = useState<TextStyleTemplate[]>([]);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // --- Chat State ---
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingConceptId, setGeneratingConceptId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Load Saved Styles from LocalStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('clicktube_text_styles');
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            setSavedStyles([...DEFAULT_TEXT_STYLES, ...parsed]);
        } catch (e) {
            setSavedStyles(DEFAULT_TEXT_STYLES);
        }
    } else {
        setSavedStyles(DEFAULT_TEXT_STYLES);
    }
  }, []);

  // Sync props concepts to local state when project or concepts change (avoid showing another project's ideas)
  useEffect(() => {
    setLocalConcepts(concepts ?? []);
  }, [projectId, concepts]);

  // Load project ideas for used/not-used status and to show past ideas
  useEffect(() => {
    if (!userId || !brandId || !projectId) return;
    getProjectIdeas(userId, brandId, projectId).then(setSavedIdeas).catch(console.warn);
  }, [userId, brandId, projectId]);

  // Load editor chat history when project context exists
  useEffect(() => {
    if (!userId || !brandId || !projectId) return;
    getProjectChatMessages(userId, brandId, projectId)
      .then((messages) => {
        if (messages.length > 0) {
          setChatMessages(messages);
        }
      })
      .catch((err) => console.warn('Failed to load chat history', err));
  }, [userId, brandId, projectId]);

  // When variation index changes, load that variation's texts
  useEffect(() => {
      if (activeVariationIndex !== lastLoadedVariationIndex && currentVariation) {
          setTexts(currentVariation.texts || []);
          setSelectedTextId(null);
          setIsEditingText(false);
          setLastLoadedVariationIndex(activeVariationIndex);
          if (activeTab === 'concepts') setActiveTab('background');
      }
  }, [activeVariationIndex, currentVariation, lastLoadedVariationIndex, activeTab]);

  // When variations first load (e.g. after generating first image), switch to background tab once
  useEffect(() => {
      const prev = prevVariationsLengthRef.current;
      if (prev === 0 && variations.length > 0 && activeTab === 'concepts') {
          setActiveTab('background');
      }
      // Do NOT update prevVariationsLengthRef here -- Effect B (clear-loading) is the single owner of that ref.
  }, [variations.length, activeTab]);

  // --- Auto Save Effect - save texts to current variation ---
  useEffect(() => {
      if (currentVariation && activeVariationIndex >= 0) {
          onAutoSave(activeVariationIndex, texts);
      }
  }, [texts, activeVariationIndex]);

  // --- Font Logic ---
  const brandFontFamily = useMemo(() => {
      const preferred = brand?.preferredFont;
      const fontConfig = HEBREW_FONTS.find(f => f.value === preferred);
      return fontConfig ? fontConfig.family : HEBREW_FONTS[0].family;
  }, [brand]);

  // Merge current-session concepts with saved ideas (so user sees past ideas and used/not-used from Firestore)
  const displayConcepts = useMemo(() => {
    const localIds = new Set((localConcepts || []).map((c) => c.id));
    const fromSaved = savedIdeas.filter((s) => !localIds.has(s.id));
    return [...(localConcepts || []), ...fromSaved];
  }, [localConcepts, savedIdeas]);

  const isConceptUsed = (c: ThumbnailConcept) =>
    executedIds.has(c.id) || savedIdeas.some((s) => s.id === c.id && s.usedAt != null);

  const openEditHook = (concept: ThumbnailConcept, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConcept(concept);
    setEditMainTitleDraft(concept.mainTitle ?? concept.title ?? '');
  };

  const updateConceptMainTitle = (concept: ThumbnailConcept, newMainTitle: string | undefined) => {
    const value = newMainTitle?.trim() || undefined;
    setLocalConcepts((prev) => {
      const inLocal = prev.find((c) => c.id === concept.id);
      if (inLocal) return prev.map((c) => (c.id === concept.id ? { ...c, mainTitle: value } : c));
      return [{ ...concept, mainTitle: value }, ...prev];
    });
  };

  const saveEditHook = () => {
    if (!editingConcept) return;
    updateConceptMainTitle(editingConcept, editMainTitleDraft);
    setEditingConcept(null);
    setEditMainTitleDraft('');
  };

  const closeEditHook = () => {
    setEditingConcept(null);
    setEditMainTitleDraft('');
  };

  useEffect(() => {
    if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // When a new variation arrives from Cloud Function (resilient image gen), clear loading and refresh credits.
  // Credits are deducted server-side; we just sync the UI.
  useEffect(() => {
    const lengthIncreased = variations.length > prevVariationsLengthRef.current;
    const conceptIdToCharge = generatingConceptId;
    if (lengthIncreased) {
      setLoading(false);
      setGeneratingConceptId(null);
      onVariationIndexChange(variations.length - 1);
      if (activeTab === 'concepts') setActiveTab('text');
      if (conceptIdToCharge) {
        setExecutedIds(prev => new Set(prev).add(conceptIdToCharge));
        // Credits already deducted server-side; refresh UI
        refreshCredits();
      }
    }
    prevVariationsLengthRef.current = variations.length;
  }, [variations.length, generatingConceptId, variations, refreshCredits, onVariationIndexChange, activeTab]);

  // --- CONCEPT GENERATION LOGIC (does not consume credits; minimum = IMAGE_GENERATION to run) ---
  const handleRegenerateConcepts = async () => {
      if (isRegenerating || !brand || !userId || !brandId || !projectId) return;
      if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_GENERATION)) {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
        return;
      }

      setIsRegenerating(true);
      try {
          const result = await generateConceptsCallable({
            projectId,
            brandId,
            params: {
              topic: localParams.topic,
              platform: localParams.platform,
              emotion: localParams.emotion,
              style: localParams.style,
              extraDetails: localParams.extraDetails ?? '',
              characterId: localParams.characterId,
              hasSecondPerson: localParams.hasSecondPerson,
              hasLogo: localParams.hasLogo,
            },
            previousConceptTitles: localConcepts.map(c => c.title),
            referenceImageUrl: currentImage?.startsWith('http') ? currentImage : undefined,
          });
          const newConcepts = result.data;
          
          // Ensure unique IDs to avoid collisions
          const uniqueNewConcepts = newConcepts.map((c: any, i: number) => ({
              ...c,
              id: `new_${Date.now()}_${i}`
          }));

          // Prepend new concepts so they appear at the top
          setLocalConcepts(prev => [...uniqueNewConcepts, ...prev]);
          await saveIdeas(userId, brandId, projectId, uniqueNewConcepts);
          await refreshCredits();
      } catch (e) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/3e021c15-0d9b-4af2-9dba-28b968201d63',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'d3a404'},body:JSON.stringify({sessionId:'d3a404',location:'Editor.tsx:generateConceptsCallable-catch',message:'Concept generation failed',data:{code:e&&typeof(e as {code?:string}).code==='string'?(e as {code:string}).code:undefined,msg:e instanceof Error?e.message:String(e).slice(0,200)},timestamp:Date.now(),hypothesisId:'timeout'})}).catch(()=>{});
          // #endregion
          reportError(e, { module: 'editor', extra: { action: 'regenerate-concepts', projectId } });
          const code = e && typeof (e as { code?: string }).code === 'string' ? (e as { code: string }).code : undefined;
          if (code === 'resource-exhausted') {
            setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
            setShowCreditsModal(true);
          } else {
            alert("שגיאה ביצירת רעיונות חדשים");
          }
      } finally {
          setIsRegenerating(false);
      }
  };

  const handleGenerateFromConcept = async (concept: ThumbnailConcept) => {
    if (loading || !brand || !userId || !brandId || !projectId) return;

    if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_GENERATION)) {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
        return;
    }

    setLoading(true);
    setGeneratingConceptId(concept.id);

    // All image generation goes through Cloud Function (server-side credits, rate limiting, no client API key)
    const isRegenSameConcept = isConceptUsed(concept);
    const referenceImageUrl = handoffReferenceImageUrl ?? (isRegenSameConcept && currentImage?.startsWith('http')
        ? currentImage
        : undefined);
    generateThumbnailImageCallable({
      projectId,
      brandId,
      concept,
      params: {
        topic: localParams.topic,
        platform: localParams.platform,
        emotion: localParams.emotion,
        style: localParams.style,
        extraDetails: localParams.extraDetails ?? '',
        characterId: localParams.characterId,
        hasSecondPerson: localParams.hasSecondPerson,
        hasLogo: localParams.hasLogo,
      },
      referenceImageUrl,
      ideaId: concept.id,
    }).catch((err) => {
      reportError(err, { module: 'editor', extra: { action: 'generate-image', projectId, conceptId: concept.id } });
      setLoading(false);
      setGeneratingConceptId(null);
      refreshCredits();
      const code = err && typeof (err as { code?: string }).code === 'string' ? (err as { code: string }).code : undefined;
      if (code === 'resource-exhausted') {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
      } else {
        alert("שגיאה ביצירת התמונה");
      }
    });
  };


  // --- STYLE TEMPLATE LOGIC ---
  const handleApplyStyle = (template: TextStyleTemplate) => {
    if (!selectedTextId) return;
    
    // Only apply style properties, keep position/text/id
    setTexts(prev => prev.map(t => {
        if (t.id === selectedTextId) {
            return {
                ...t,
                ...template.style,
                // Ensure text/pos properties aren't overwritten just in case
                id: t.id,
                text: t.text,
                x: t.x,
                y: t.y,
                fontSize: t.fontSize, // Maybe allow font size to change? For now, keep user size.
                rotation: t.rotation
            };
        }
        return t;
    }));
  };

  const handleSaveStyle = () => {
    if (!selectedText) return;
    
    const name = prompt("תן שם לסגנון החדש:");
    if (!name) return;

    // Extract style properties
    const { id, text, x, y, rotation, fontSize, isDragging, ...styleProps } = selectedText;

    const newTemplate: TextStyleTemplate = {
        id: Date.now().toString(),
        name,
        style: styleProps
    };

    const updatedCustomStyles = [...savedStyles.filter(s => !s.isDefault), newTemplate];
    
    // Update State
    setSavedStyles([...DEFAULT_TEXT_STYLES, ...updatedCustomStyles]);
    
    // Persist Custom Only
    localStorage.setItem('clicktube_text_styles', JSON.stringify(updatedCustomStyles));
  };

  const handleDeleteStyle = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (confirm("למחוק את הסגנון הזה?")) {
          const updatedCustomStyles = savedStyles.filter(s => !s.isDefault && s.id !== id);
          setSavedStyles([...DEFAULT_TEXT_STYLES, ...updatedCustomStyles]);
          localStorage.setItem('clicktube_text_styles', JSON.stringify(updatedCustomStyles));
      }
  };

  // --- TEXT LOGIC ---
  const addText = () => {
    // Smart Defaults based on Image Size
    const width = imageDimensions?.width || 1280;
    const height = imageDimensions?.height || 720;
    const fontSize = 100; // Fixed default

    const newText: TextObject = {
        id: Date.now().toString(),
        text: 'לחץ פעמיים\nלעריכה',
        x: width / 2, // Center
        y: height / 2,
        fontSize: fontSize,
        color: '#FFFFFF',
        fontFamily: brandFontFamily, // Use Brand Font
        isDragging: false,
        strokeColor: '#000000',
        strokeWidth: 0, // Clean by default for manual additions
        rotation: 0,
        // Background defaults
        isBackgroundEnabled: false,
        backgroundColor: '#000000',
        backgroundOpacity: 100,
        backgroundPadding: Math.round(fontSize * 0.2),
        backgroundRadius: Math.round(fontSize * 0.1),
        // Shadow defaults
        isShadowEnabled: false,
        shadowColor: '#000000',
        shadowBlur: 10,
        shadowOpacity: 0.8,
        shadowDistance: 10,
        shadowAngle: 45,
        // Neon defaults
        neonColor: '#FF003C',
        neonIntensity: 0
    };
    setTexts([...texts, newText]);
    setSelectedTextId(newText.id);
    setActiveTab('text'); // Switch to text tab
  };

  const duplicateText = () => {
      if (!selectedTextId) return;
      const original = texts.find(t => t.id === selectedTextId);
      if (!original) return;

      const offset = (imageDimensions?.width || 100) * 0.02; // 2% offset

      const newText: TextObject = {
          ...original,
          id: Date.now().toString(),
          x: original.x + offset, 
          y: original.y + offset
      };
      
      setTexts([...texts, newText]);
      setSelectedTextId(newText.id);
  };

  const updateSelectedText = (key: keyof TextObject, value: any) => {
      if (!selectedTextId) return;
      setTexts(prev => prev.map(t => t.id === selectedTextId ? { ...t, [key]: value } : t));
  };

  const alignText = (axis: 'horizontal' | 'vertical') => {
      if (!selectedTextId || !imageDimensions) return;
      
      if (axis === 'horizontal') {
          updateSelectedText('x', imageDimensions.width / 2);
      } else {
          updateSelectedText('y', imageDimensions.height / 2);
      }
  };

  const removeSelectedText = () => {
      if (!selectedTextId) return;
      setTexts(prev => prev.filter(t => t.id !== selectedTextId));
      setSelectedTextId(null);
      setIsEditingText(false);
  };

  const resetRotation = () => {
      updateSelectedText('rotation', 0);
  };
  
  const selectedText = texts.find(t => t.id === selectedTextId);

  // --- DOWNLOAD LOGIC ---
  const handleDownloadClick = () => {
      // If no text, just download clean image immediately
      if (texts.length === 0) {
          downloadCleanImage();
          return;
      }
      
      // Toggle Menu
      setShowDownloadMenu(!showDownloadMenu);
  };

  const downloadCleanImage = async () => {
      try {
          const response = await fetch(currentImage);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = url;
          link.download = `clicktube_clean_${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
          }, 100);
      } catch (e) {
          reportError(e, { module: 'editor', extra: { action: 'download-image' } }, 'warning');
          // Fallback
          const link = document.createElement('a');
          link.download = `clicktube_clean_${Date.now()}.png`;
          link.href = currentImage;
          link.click();
      }
      setShowDownloadMenu(false);
  };

  const downloadWithText = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const currentSelection = selectedTextId;
      setSelectedTextId(null);
      
      setTimeout(() => {
          const link = document.createElement('a');
          link.download = `clicktube_final_${Date.now()}.png`;
          link.href = canvas.toDataURL('image/png', 1.0);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setSelectedTextId(currentSelection);
          setShowDownloadMenu(false);
      }, 50);
  };

  // --- CHAT LOGIC ---
  const handleSend = async () => {
    if (!input.trim() || loading || !currentImage || !userId || !brandId || !projectId) return;

    // Check credits before AI operation (local check; server is authority)
    if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_EDIT)) {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_EDIT);
        setShowCreditsModal(true);
        return;
    }
    
    const userMsg: ChatMessage = { role: 'user', text: input };
    setChatMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    saveChatMessage(userId, brandId, projectId, { role: 'user', text: userMsg.text }).catch((err) => console.warn('Failed to save chat message', err));

    try {
        // Use Cloud Function for image editing (server-side credits, rate limiting, no client API key)
        const result = await editThumbnailImageCallable({
          projectId,
          brandId,
          imageUrl: currentImage,
          instruction: userMsg.text,
          params: {
            topic: localParams.topic,
            platform: localParams.platform,
            emotion: localParams.emotion,
            style: localParams.style,
            extraDetails: localParams.extraDetails ?? '',
            hasSecondPerson: localParams.hasSecondPerson,
            hasLogo: localParams.hasLogo,
          },
        });
        const newImage = result.data.imageUrl;

        // Credits deducted server-side; refresh UI
        await refreshCredits();

        // Create new variation with current texts; pass current variation's concept so "התאם אוטומטית" uses the right idea
        onNewVariation(newImage, texts, currentVariation?.concept ?? concept);
        const modelText = 'הנה הגרסה המעודכנת מהמעבדה:';
        setChatMessages(prev => [...prev, { role: 'model', text: modelText, image: newImage }]);
        const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        saveChatMessage(userId, brandId, projectId, { role: 'model', text: modelText, imageUrl: newImage }, messageId).catch((err) => console.warn('Failed to save chat message', err));
    } catch (e) {
        await refreshCredits();
        const code = e && typeof (e as { code?: string }).code === 'string' ? (e as { code: string }).code : undefined;
        if (code === 'resource-exhausted') {
          setCreditsNeeded(CREDIT_COSTS.IMAGE_EDIT);
          setShowCreditsModal(true);
        } else {
          const errorText = 'שגיאה בעריכת התמונה.';
          setChatMessages(prev => [...prev, { role: 'model', text: errorText }]);
          saveChatMessage(userId, brandId, projectId, { role: 'model', text: errorText }).catch((err) => console.warn('Failed to save chat message', err));
        }
    } finally {
        setLoading(false);
    }
  };

  const handleAdaptPlatform = async () => {
    if (loading || !brand || !currentImage || !userId || !brandId || !projectId) return;

    // Check credits before AI operation (local check; server is authority)
    if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_GENERATION)) {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
        return;
    }

    const targetPlatform = localParams.platform === Platform.YOUTUBE ? Platform.INSTAGRAM : Platform.YOUTUBE;
    const buttonText = `התאמה ל-${targetPlatform}`;
    setChatMessages(prev => [...prev, { role: 'user', text: buttonText }]);
    setLoading(true);
    saveChatMessage(userId, brandId, projectId, { role: 'user', text: buttonText }).catch((err) => console.warn('Failed to save chat message', err));

    try {
        const newParams = { ...localParams, platform: targetPlatform };
        const fallbackConcept: ThumbnailConcept = {
            id: 'temp_adapt', title: localParams.topic, descriptionHe: 'התאמה אוטומטית',
            mainTitle: localParams.topic,
            visualDescription: `A high quality image representing: ${localParams.topic}. ${localParams.extraDetails ?? ''}`, elements: [], rationale: []
        };

        let newImage: string;
        let conceptToUse: ThumbnailConcept;

        if (currentVariation?.concept) {
          // Re-generate with concept for new platform via Cloud Function
          conceptToUse = currentVariation.concept;
          const result = await generateThumbnailImageCallable({
            projectId,
            brandId,
            concept: conceptToUse,
            params: {
              topic: newParams.topic,
              platform: newParams.platform,
              emotion: newParams.emotion,
              style: newParams.style,
              extraDetails: newParams.extraDetails ?? '',
              characterId: newParams.characterId,
              hasSecondPerson: newParams.hasSecondPerson,
              hasLogo: newParams.hasLogo,
            },
            referenceImageUrl: currentImage.startsWith('http') ? currentImage : undefined,
          });
          newImage = result.data.imageUrl;
        } else {
          // Adapt via dedicated Cloud Function
          const result = await adaptThumbnailToPlatformCallable({
            projectId,
            brandId,
            imageUrl: currentImage,
            targetPlatform,
            params: {
              topic: localParams.topic,
              platform: localParams.platform,
              emotion: localParams.emotion,
              style: localParams.style,
              extraDetails: localParams.extraDetails ?? '',
              hasSecondPerson: localParams.hasSecondPerson,
              hasLogo: localParams.hasLogo,
            },
          });
          newImage = result.data.imageUrl;
          conceptToUse = fallbackConcept;
        }

        // Credits deducted server-side; refresh UI
        await refreshCredits();

        const modelText = `התמונה הותאמה ל-${targetPlatform}.`;
        setChatMessages(prev => [...prev, { role: 'model', text: modelText, image: newImage }]);
        onNewVariation(newImage, texts, conceptToUse);
        setLocalParams(newParams);
        const messageId = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        saveChatMessage(userId, brandId, projectId, { role: 'model', text: modelText, imageUrl: newImage }, messageId).catch((err) => console.warn('Failed to save chat message', err));
    } catch (e) {
        await refreshCredits();
        const code = e && typeof (e as { code?: string }).code === 'string' ? (e as { code: string }).code : undefined;
        if (code === 'resource-exhausted') {
          setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
          setShowCreditsModal(true);
        } else {
          const errorText = 'שגיאה בהתאמת הפלטפורמה.';
          setChatMessages(prev => [...prev, { role: 'model', text: errorText }]);
          saveChatMessage(userId, brandId, projectId, { role: 'model', text: errorText }).catch((err) => console.warn('Failed to save chat message', err));
        }
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="h-full min-h-[60vh] flex flex-col md:flex-row overflow-hidden bg-click-dark">
        {/* Left: Canvas Area */}
        <div className="flex-1 min-h-0 min-w-0 p-4 md:p-8 bg-black/50 flex flex-col items-center relative border-l border-click-border min-h-[280px] md:min-h-0">
            {/* Toolbar */}
            <div className="w-full flex justify-between mb-4 gap-2 sm:gap-4 relative z-50 flex-wrap items-center">
                 <button onClick={onBack} className="bg-click-border hover:bg-white/20 p-1.5 sm:p-2 rounded text-white flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm shrink-0 min-h-[44px] min-w-[44px] sm:min-w-0">
                    <ICONS.ArrowRight className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">חזרה</span>
                 </button>
                 
                 <div className="flex gap-1.5 sm:gap-2 relative items-center shrink-0">
                     {/* Saved Indicator - hide on very small */}
                     <span className="hidden sm:flex items-center gap-1 text-xs text-gray-500 mr-1 sm:mr-2">
                        {isSaving ? (
                          <><ICONS.Loader2 className="w-3 h-3 text-yellow-400 animate-spin" /> שומר...</>
                        ) : (
                          <><ICONS.Check className="w-3 h-3 text-green-500" /> נשמר אוטומטית</>
                        )}
                     </span>

                     <button 
                        onClick={handleDownloadClick} 
                        disabled={!currentImage} 
                        className="bg-click-red text-white font-bold px-3 py-1.5 sm:px-4 sm:py-2 rounded flex items-center gap-1.5 sm:gap-2 hover:bg-white hover:text-click-red transition-colors shadow-[0_0_10px_rgba(255,0,60,0.3)] text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                     >
                        <ICONS.Download className="w-4 h-4 shrink-0" /> הורד
                     </button>
                     
                     {/* Download Dropdown */}
                     {showDownloadMenu && texts.length > 0 && (
                         <div className="absolute top-full right-0 left-auto mt-2 w-48 bg-click-card border border-click-border rounded-lg shadow-xl overflow-hidden z-50 animate-in slide-in-from-top-2">
                             <button 
                                onClick={downloadCleanImage}
                                className="w-full text-right px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/10 flex items-center gap-2 border-b border-click-border/50"
                             >
                                 <ICONS.Image className="w-4 h-4 text-click-cyan" /> תמונה נקייה
                             </button>
                             <button 
                                onClick={downloadWithText}
                                className="w-full text-right px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/10 flex items-center gap-2"
                             >
                                 <ICONS.Type className="w-4 h-4 text-click-red" /> תמונה עם טקסט
                             </button>
                         </div>
                     )}
                     {showDownloadMenu && texts.length > 0 && (
                         <div className="fixed inset-0 z-40" onClick={() => setShowDownloadMenu(false)}></div>
                     )}
                 </div>
            </div>

            {/* Main Image Container / Canvas Editor */}
            <div className="relative shadow-2xl transition-all w-full flex-1 flex flex-col overflow-hidden min-h-[35vh] md:min-h-0">
                {loading && (
                    <div className="absolute inset-0 bg-black/70 z-50 flex items-center justify-center flex-col text-click-red rounded-lg backdrop-blur-sm border-2 border-click-red/20">
                        <ICONS.Loader2 className="w-12 h-12 animate-spin mb-2" />
                        <span className="font-display tracking-widest animate-pulse">
                            {generatingConceptId ? 'GENERATING CONCEPT...' : 'GENERATING VARIATION...'}
                        </span>
                    </div>
                )}
                
                {currentImage ? (
                    <CanvasEditor 
                        imageUrl={currentImage} 
                        canvasRef={canvasRef}
                        texts={texts}
                        selectedTextId={selectedTextId}
                        onTextsChange={setTexts}
                        onSelectText={(id) => {
                            setSelectedTextId(id);
                            if (id) setActiveTab('text');
                        }}
                        isEditingText={isEditingText}
                        setIsEditingText={setIsEditingText}
                        onTextUpdate={(id, txt) => {
                            setTexts(prev => prev.map(t => t.id === id ? { ...t, text: txt } : t));
                        }}
                        onImageLoaded={(w, h) => {
                            setImageDimensions({width: w, height: h});
                            // Sync platform state with the actual image dimensions
                            // This ensures that if the user selects an old image from history,
                            // the editor state (and subsequent edits) matches that image's format.
                            const isVertical = h > w;
                            const detectedPlatform = isVertical ? Platform.INSTAGRAM : Platform.YOUTUBE;
                            
                            if (localParams.platform !== detectedPlatform) {
                                setLocalParams(prev => ({ ...prev, platform: detectedPlatform }));
                            }
                        }}
                    />
                ) : (
                    // EMPTY STATE: CONCEPT SELECTION
                    <div className="w-full h-full flex flex-col items-center justify-center p-4 sm:p-6 md:p-10 bg-lab-pattern relative overflow-hidden">
                        <div className="text-center z-10 mb-4 md:mb-8">
                             <div className="w-16 h-16 bg-click-red/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-click-red/50">
                                 <ICONS.FlaskConical className="w-8 h-8 text-click-red" />
                             </div>
                             <h2 className="text-2xl font-bold text-white mb-2">המעבדה מוכנה לניסוי</h2>
                             <p className="text-gray-400">בחר קונספט להתחלה או השתמש בצ'אט בצד ימין</p>
                        </div>
                        
                        {displayConcepts.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl w-full z-10">
                                {displayConcepts.map((concept) => (
                                    <div 
                                        key={concept.id}
                                        onClick={() => !loading && handleGenerateFromConcept(concept)}
                                        className={`bg-click-card border border-click-border rounded-xl p-5 transition-all group relative overflow-hidden ${
                                            loading
                                                ? 'opacity-60 cursor-not-allowed pointer-events-none'
                                                : 'hover:border-click-cyan hover:shadow-[0_0_20px_rgba(0,240,255,0.2)] cursor-pointer'
                                        }`}
                                    >
                                        <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-r from-click-cyan to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            {isConceptUsed(concept) && (
                                                <span className="shrink-0 bg-green-500 text-black text-caption font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <ICONS.Check className="w-3 h-3" /> נוצר
                                                </span>
                                            )}
                                            <h3 className="font-bold text-white flex-1 min-w-0 group-hover:text-click-cyan transition-colors">{concept.mainTitle ?? concept.title}</h3>
                                            <button
                                                type="button"
                                                onClick={(e) => openEditHook(concept, e)}
                                                className="shrink-0 p-1 rounded text-gray-500 hover:text-click-cyan hover:bg-white/10 transition-colors"
                                                title="ערוך כותרת (הוק)"
                                            >
                                                <ICONS.Edit2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {concept.mainTitle && concept.title && (
                                            <p className="text-sm text-gray-400 mb-2 line-clamp-2">{concept.title}</p>
                                        )}
                                        <div className="flex flex-wrap gap-1.5">
                                            {(concept.elements || []).slice(0, 4).map((el, i) => (
                                                <span key={i} className="text-caption bg-click-border text-gray-300 px-2 py-1 rounded-full border border-click-border">
                                                    {el}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Timeline Strip */}
            <div className="w-full max-w-5xl mt-6">
                <div className="flex items-center justify-between mb-2">
                     <span className="text-xs text-gray-400 font-bold uppercase tracking-wider flex items-center gap-2">
                        <ICONS.LayoutGrid className="w-3 h-3 text-click-red" />
                        היסטוריית גרסאות ({variations.length})
                        {isSaving && <span className="text-yellow-400 animate-pulse">שומר...</span>}
                     </span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x">
                    {variations.map((variation, idx) => (
                        <div 
                            key={variation.id} 
                            onClick={() => onVariationIndexChange(idx)}
                            className={`flex-shrink-0 w-24 h-24 rounded-md overflow-hidden cursor-pointer border-2 transition-all snap-start group relative bg-click-dark ${
                                idx === activeVariationIndex 
                                ? 'border-click-cyan shadow-[0_0_10px_#00F0FF] scale-105' 
                                : 'border-click-border hover:border-white opacity-60 hover:opacity-100'
                            }`}
                        >
                            <img src={variation.imageUrl} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end justify-center pb-1">
                                <span className="text-caption text-white font-mono">V.{idx + 1}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Right: Tabbed Sidebar */}
        <div className="w-full md:w-[350px] flex flex-col bg-click-card border-r border-click-border shadow-xl z-10 max-h-[55vh] md:max-h-none overflow-y-auto md:overflow-visible">
            
            {/* Tabs Header */}
            <div className="flex border-b border-click-border bg-click-dark">
                 <button 
                    onClick={() => setActiveTab('concepts')}
                    className={`flex-1 min-h-[44px] py-4 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                        activeTab === 'concepts' 
                        ? 'text-white border-b-2 border-yellow-400 bg-click-card' 
                        : 'text-gray-500 hover:text-white hover:bg-click-card/50'
                    }`}
                >
                    <ICONS.Sparkles className="w-4 h-4" /> רעיונות
                </button>
                <button 
                    onClick={() => setActiveTab('background')}
                    className={`flex-1 min-h-[44px] py-4 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                        activeTab === 'background' 
                        ? 'text-white border-b-2 border-click-red bg-click-card' 
                        : 'text-gray-500 hover:text-white hover:bg-click-card/50'
                    }`}
                >
                    <ICONS.Wand2 className="w-4 h-4" /> רקע (AI)
                </button>
                <button 
                    onClick={() => setActiveTab('text')}
                    className={`flex-1 min-h-[44px] py-4 flex items-center justify-center gap-2 text-sm font-bold transition-colors ${
                        activeTab === 'text' 
                        ? 'text-white border-b-2 border-click-cyan bg-click-card' 
                        : 'text-gray-500 hover:text-white hover:bg-click-card/50'
                    }`}
                >
                    <ICONS.Type className="w-4 h-4" /> טקסט
                </button>
            </div>

            {/* TAB CONTENT: CONCEPTS */}
            {activeTab === 'concepts' && (
                 <div className="flex-1 overflow-y-auto p-4 scrollbar-hide bg-click-dark/30">
                     {/* Refresh Button */}
                     <button 
                        onClick={handleRegenerateConcepts}
                        disabled={isRegenerating}
                        className="w-full mb-4 bg-click-card border border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 hover:border-yellow-400 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-[0_0_15px_rgba(250,204,21,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        {isRegenerating ? (
                            <ICONS.Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <ICONS.Sparkles className="w-4 h-4" />
                        )}
                        {isRegenerating ? 'רוקח נוסחאות...' : 'תן לי 3 רעיונות חדשים'}
                     </button>

                     <h3 className="text-gray-400 text-xs font-bold uppercase mb-4 tracking-wider">נוסחאות מוכנות לשימוש</h3>
                     <div className="space-y-4">
                        {displayConcepts.map((c) => {
                            const isUsed = isConceptUsed(c);
                            return (
                                <div 
                                    key={c.id} 
                                    onClick={() => !loading && handleGenerateFromConcept(c)}
                                    className={`relative bg-click-card border p-4 rounded-xl transition-all group overflow-hidden ${
                                        loading
                                            ? 'opacity-60 cursor-not-allowed pointer-events-none border-click-border'
                                            : isUsed 
                                                ? 'border-green-500/50 bg-green-500/5 cursor-default' 
                                                : 'border-click-border hover:border-yellow-400/50 hover:bg-click-dark cursor-pointer'
                                    }`}
                                >
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        {isUsed && (
                                            <span className="shrink-0 bg-green-500 text-black text-caption font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                                <ICONS.Check className="w-3 h-3" /> נוצר
                                            </span>
                                        )}
                                        <h4 className={`font-bold text-sm flex-1 min-w-0 transition-colors ${isUsed ? 'text-green-400' : 'text-white group-hover:text-yellow-400'}`}>
                                            {c.mainTitle ?? c.title}
                                        </h4>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); openEditHook(c, e); }}
                                                className="p-1 rounded text-gray-500 hover:text-yellow-400 hover:bg-white/10 transition-colors"
                                                title="ערוך כותרת (הוק)"
                                            >
                                                <ICONS.Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            {!isUsed && <ICONS.Zap className="w-4 h-4 text-gray-600 group-hover:text-yellow-400" />}
                                        </div>
                                    </div>
                                    {c.mainTitle && c.title && (
                                        <p className="text-xs text-gray-400 mb-2 line-clamp-2">{c.title}</p>
                                    )}
                                    <div className="flex flex-wrap gap-1.5">
                                        {(c.elements || []).slice(0, 4).map((el, i) => (
                                            <span key={i} className="text-caption bg-black/50 border border-click-border px-2 py-0.5 rounded text-gray-400">
                                                {el}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                     </div>
                 </div>
            )}

            {/* TAB CONTENT: BACKGROUND (CHAT) */}
            {activeTab === 'background' && (
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide bg-click-dark/50" ref={scrollRef}>
                        {chatMessages.map((msg, idx) => (
                            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                                <div className={`max-w-[90%] p-3 rounded-lg text-sm shadow-md ${
                                    msg.role === 'user' 
                                    ? 'bg-click-border text-white rounded-br-none border border-gray-600' 
                                    : 'bg-click-red/10 border border-click-red/30 text-gray-200 rounded-bl-none'
                                }`}>
                                    {msg.text}
                                    {msg.image && (
                                        <div className="mt-2 relative group">
                                            <img src={msg.image} className="rounded border border-click-red/20 w-full" />
                                            <span className="absolute bottom-1 left-1 text-caption bg-black/70 px-1 rounded text-gray-400">נוסף להיסטוריה</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-4 border-t border-click-border bg-click-dark">
                         {brand && (
                            <button 
                                onClick={handleAdaptPlatform}
                                disabled={loading}
                                className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2 bg-click-dark border border-click-border text-gray-300 hover:text-white hover:border-white rounded transition-all text-xs"
                            >
                                <ICONS.LayoutGrid className="w-3 h-3" />
                                {localParams.platform === Platform.YOUTUBE ? 'התאם אוטומטית לאינסטגרם' : 'התאם אוטומטית ליוטיוב'}
                            </button>
                        )}
                        <div className="relative">
                            <input 
                                type="text" 
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSend()}
                                className="w-full bg-click-card border border-click-border text-white p-3 pr-10 rounded focus:border-click-red outline-none placeholder-gray-600 transition-colors"
                                placeholder="למשל: שנה את הרקע לכחול..."
                            />
                            <button 
                                onClick={handleSend}
                                disabled={loading}
                                className="absolute left-2 top-2 p-1 text-click-red hover:text-white disabled:opacity-50 transition-colors"
                            >
                                {loading ? <ICONS.Loader2 className="w-5 h-5 animate-spin"/> : <ICONS.Zap className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: TEXT EDITOR */}
            {activeTab === 'text' && (
                <div className="flex-1 overflow-y-auto p-4 scrollbar-hide bg-click-card">
                    <button onClick={addText} className="w-full mb-4 bg-click-cyan text-black font-bold py-3 rounded flex items-center justify-center gap-2 hover:bg-white transition-colors">
                        <ICONS.Plus className="w-4 h-4" /> הוספת טקסט חדש
                    </button>

                    {/* --- STYLE PRESETS SECTION (New) --- */}
                    <div className="mb-6">
                         <div className="flex justify-between items-center mb-2">
                             <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                <ICONS.Bookmark className="w-3 h-3"/> סגנונות מוכנים
                            </label>
                            {selectedText && (
                                <button onClick={handleSaveStyle} className="text-caption flex items-center gap-1 text-click-cyan hover:text-white transition-colors">
                                    <ICONS.Save className="w-3 h-3" /> שמור סגנון
                                </button>
                            )}
                         </div>
                         <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {savedStyles.map((style) => (
                                <button
                                    key={style.id}
                                    onClick={() => handleApplyStyle(style)}
                                    className="relative aspect-square rounded-lg border border-click-border hover:border-click-cyan bg-click-dark overflow-hidden group transition-all"
                                    title={style.name}
                                >
                                    {/* Preview Approximation */}
                                    <div className="absolute inset-0 flex items-center justify-center text-caption font-bold p-1 leading-tight select-none"
                                        style={{
                                            fontFamily: style.style.fontFamily,
                                            color: style.style.color,
                                            backgroundColor: style.style.isBackgroundEnabled ? style.style.backgroundColor : 'transparent',
                                            textShadow: style.style.neonIntensity && style.style.neonIntensity > 0 
                                                ? `0 0 10px ${style.style.neonColor}` 
                                                : style.style.isShadowEnabled && style.style.shadowOpacity && style.style.shadowOpacity > 0
                                                    ? `${style.style.shadowDistance}px ${style.style.shadowDistance}px 4px ${style.style.shadowColor}`
                                                    : 'none',
                                            WebkitTextStroke: style.style.strokeWidth && style.style.strokeWidth > 0 ? `1px ${style.style.strokeColor}` : '0',
                                        }}
                                    >
                                        ABC
                                    </div>
                                    
                                    {!style.isDefault && (
                                        <div 
                                            onClick={(e) => handleDeleteStyle(style.id, e)}
                                            className="absolute top-0 right-0 p-1 bg-black/80 text-red-500 opacity-0 group-hover:opacity-100 hover:text-white transition-all rounded-bl-md"
                                        >
                                            <ICONS.X className="w-3 h-3" />
                                        </div>
                                    )}
                                </button>
                            ))}
                         </div>
                    </div>

                    {selectedText ? (
                        <div className="space-y-6 animate-in slide-in-from-right-4 pb-20">
                            
                            {/* Section 1: Typography & Basic Design */}
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <ICONS.Type className="w-3 h-3"/> עיצוב בסיסי
                                </label>
                                <div className="bg-click-dark p-3 rounded-lg border border-click-border space-y-4">
                                    
                                    {/* Font Select */}
                                    <div>
                                        <label className="text-caption text-gray-500 mb-1 block">פונט</label>
                                        <select 
                                            value={selectedText.fontFamily} 
                                            onChange={(e) => updateSelectedText('fontFamily', e.target.value)}
                                            className="w-full bg-click-card border border-click-border text-white text-sm px-2 py-2 rounded focus:border-click-cyan outline-none"
                                            style={{ fontFamily: selectedText.fontFamily }}
                                        >
                                            {HEBREW_FONTS.map(f => (
                                                <option key={f.value} value={f.family} style={{ fontFamily: f.family }}>{f.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Color & Size Row */}
                                    <div className="flex items-end gap-3">
                                        {/* Color Picker */}
                                        <div className="shrink-0">
                                            <label className="text-caption text-gray-500 mb-1 block">צבע</label>
                                            <div 
                                                className="w-10 h-10 rounded-lg border border-click-border relative cursor-pointer shadow-sm hover:border-click-cyan transition-all overflow-hidden"
                                                style={{ backgroundColor: selectedText.color }}
                                            >
                                                <input 
                                                    type="color" 
                                                    value={selectedText.color}
                                                    onChange={(e) => updateSelectedText('color', e.target.value)}
                                                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                                                    title="בחר צבע"
                                                />
                                            </div>
                                        </div>

                                        {/* Size Slider */}
                                        <div className="flex-1">
                                            <div className="flex justify-between mb-1">
                                                <label className="text-caption text-gray-500">גודל</label>
                                                <span className="text-caption text-gray-400">{selectedText.fontSize}</span>
                                            </div>
                                            <input 
                                                type="range" 
                                                min="20" max="400" 
                                                value={selectedText.fontSize}
                                                onChange={(e) => updateSelectedText('fontSize', Number(e.target.value))}
                                                className="w-full accent-click-cyan h-2 bg-click-border rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>

                                    {/* ALIGNMENT BUTTONS */}
                                    <div className="flex gap-2 pt-2 border-t border-click-border/30">
                                         <button 
                                            onClick={() => alignText('horizontal')}
                                            className="flex-1 py-2 bg-click-border text-gray-400 hover:text-white hover:bg-click-card rounded flex items-center justify-center gap-1 transition-colors"
                                            title="מרכז אופקית"
                                         >
                                             <ICONS.AlignHorizontal className="w-4 h-4" />
                                             <span className="text-caption">מרכז אופקי</span>
                                         </button>
                                         <button 
                                            onClick={() => alignText('vertical')}
                                            className="flex-1 py-2 bg-click-border text-gray-400 hover:text-white hover:bg-click-card rounded flex items-center justify-center gap-1 transition-colors"
                                            title="מרכז אנכית"
                                         >
                                             <ICONS.AlignVertical className="w-4 h-4" />
                                             <span className="text-caption">מרכז אנכי</span>
                                         </button>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Additional Effects */}
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-gray-400 uppercase flex items-center gap-2">
                                    <ICONS.Layers className="w-3 h-3"/> אפקטים נוספים
                                </label>
                                <div className="bg-click-dark p-3 rounded-lg border border-click-border space-y-4">
                                    
                                    {/* 1. Background */}
                                    <div>
                                        <label className="text-xs text-white font-bold mb-2 flex items-center gap-2 cursor-pointer select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedText.isBackgroundEnabled}
                                                onChange={(e) => updateSelectedText('isBackgroundEnabled', e.target.checked)}
                                                className="accent-click-cyan w-3 h-3 cursor-pointer"
                                            />
                                            רקע לטקסט
                                        </label>
                                        
                                        {selectedText.isBackgroundEnabled && (
                                            <div className="space-y-3 pl-5 pt-2 border-l border-click-border ml-1.5 animate-in slide-in-from-top-1">
                                                 <div className="flex items-center justify-between">
                                                    <span className="text-caption text-gray-400">צבע רקע</span>
                                                    <input 
                                                        type="color" 
                                                        value={selectedText.backgroundColor}
                                                        onChange={(e) => updateSelectedText('backgroundColor', e.target.value)}
                                                        className="w-5 h-5 rounded cursor-pointer border-none bg-transparent"
                                                    />
                                                 </div>
                                                 
                                                 <div className="flex items-center gap-2">
                                                    <span className="text-caption text-gray-500 w-10">שקיפות</span>
                                                    <input 
                                                        type="range" min="0" max="100" 
                                                        value={selectedText.backgroundOpacity} 
                                                        onChange={(e) => updateSelectedText('backgroundOpacity', Number(e.target.value))} 
                                                        className="flex-1 h-1 accent-gray-400 bg-gray-700 rounded appearance-none" 
                                                    />
                                                 </div>

                                                 <div className="flex items-center gap-2">
                                                    <span className="text-caption text-gray-500 w-10">מרווח</span>
                                                    <input 
                                                        type="range" min="0" max="50" 
                                                        value={selectedText.backgroundPadding} 
                                                        onChange={(e) => updateSelectedText('backgroundPadding', Number(e.target.value))} 
                                                        className="flex-1 h-1 accent-gray-400 bg-gray-700 rounded appearance-none" 
                                                    />
                                                 </div>

                                                 <div className="flex items-center gap-2">
                                                    <span className="text-caption text-gray-500 w-10">פינות</span>
                                                    <input 
                                                        type="range" min="0" max="50" 
                                                        value={selectedText.backgroundRadius} 
                                                        onChange={(e) => updateSelectedText('backgroundRadius', Number(e.target.value))} 
                                                        className="flex-1 h-1 accent-gray-400 bg-gray-700 rounded appearance-none" 
                                                    />
                                                 </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="h-px bg-click-border/50"></div>

                                    {/* 2. Stroke */}
                                    <div>
                                        <label className="text-xs text-white font-bold mb-2 flex items-center gap-2 cursor-pointer select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedText.strokeWidth > 0}
                                                onChange={(e) => updateSelectedText('strokeWidth', e.target.checked ? 8 : 0)}
                                                className="accent-click-cyan w-3 h-3 cursor-pointer"
                                            />
                                            קו מתאר (Stroke)
                                        </label>

                                        {selectedText.strokeWidth > 0 && (
                                            <div className="space-y-3 pl-5 pt-2 border-l border-click-border ml-1.5 animate-in slide-in-from-top-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-caption text-gray-400">צבע</span>
                                                    <input 
                                                        type="color" 
                                                        value={selectedText.strokeColor}
                                                        onChange={(e) => updateSelectedText('strokeColor', e.target.value)}
                                                        className="w-5 h-5 rounded cursor-pointer border-none bg-transparent"
                                                    />
                                                 </div>
                                                 <div className="flex items-center gap-2">
                                                    <span className="text-caption text-gray-500 w-10">עובי</span>
                                                    <input 
                                                        type="range" 
                                                        min="0" max="40" step="0.5"
                                                        value={selectedText.strokeWidth}
                                                        onChange={(e) => updateSelectedText('strokeWidth', Number(e.target.value))}
                                                        className="flex-1 h-1 accent-gray-400 bg-gray-700 rounded appearance-none"
                                                    />
                                                 </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="h-px bg-click-border/50"></div>

                                    {/* 3. Shadow */}
                                    <div>
                                         <label className="text-xs text-white font-bold mb-2 flex items-center gap-2 cursor-pointer select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedText.isShadowEnabled}
                                                onChange={(e) => updateSelectedText('isShadowEnabled', e.target.checked)}
                                                className="accent-click-cyan w-3 h-3 cursor-pointer"
                                            />
                                            הצללה (Shadow)
                                         </label>
                                         
                                         {selectedText.isShadowEnabled && (
                                            <div className="space-y-2 pl-5 pt-2 border-l border-click-border ml-1.5 animate-in slide-in-from-top-1">
                                                 <div className="flex items-center justify-between">
                                                    <span className="text-caption text-gray-400">צבע</span>
                                                    <input 
                                                        type="color" 
                                                        value={selectedText.shadowColor}
                                                        onChange={(e) => updateSelectedText('shadowColor', e.target.value)}
                                                        className="w-5 h-5 rounded cursor-pointer border-none bg-transparent"
                                                    />
                                                 </div>
                                                 {[{l:'מרחק', k:'shadowDistance', max:50}, {l:'זווית', k:'shadowAngle', max:360}, {l:'טשטוש', k:'shadowBlur', max:50}].map(item => (
                                                     <div key={item.k} className="flex items-center gap-2">
                                                        <span className="text-caption text-gray-500 w-10">{item.l}</span>
                                                        <input 
                                                            type="range" min="0" max={item.max} 
                                                            value={(selectedText as any)[item.k]} 
                                                            onChange={(e) => updateSelectedText(item.k as any, Number(e.target.value))} 
                                                            className="flex-1 h-1 accent-gray-400 bg-gray-700 rounded appearance-none" 
                                                        />
                                                     </div>
                                                 ))}
                                            </div>
                                         )}
                                    </div>

                                    <div className="h-px bg-click-border/50"></div>

                                    {/* 4. Neon */}
                                    <div>
                                         <label className="text-xs text-white font-bold mb-2 flex items-center gap-2 cursor-pointer select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedText.neonIntensity > 0}
                                                onChange={(e) => updateSelectedText('neonIntensity', e.target.checked ? 20 : 0)}
                                                className="accent-click-cyan w-3 h-3 cursor-pointer"
                                            />
                                            ניאון (Glow)
                                         </label>

                                         {selectedText.neonIntensity > 0 && (
                                            <div className="space-y-3 pl-5 pt-2 border-l border-click-border ml-1.5 animate-in slide-in-from-top-1">
                                                 <div className="flex items-center justify-between">
                                                    <span className="text-caption text-gray-400">צבע</span>
                                                    <input 
                                                        type="color" 
                                                        value={selectedText.neonColor}
                                                        onChange={(e) => updateSelectedText('neonColor', e.target.value)}
                                                        className="w-5 h-5 rounded cursor-pointer border-none bg-transparent"
                                                    />
                                                 </div>
                                                 <div className="flex items-center gap-2">
                                                    <span className="text-caption text-gray-500 w-10">עוצמה</span>
                                                    <input 
                                                        type="range" min="0" max="50" 
                                                        value={selectedText.neonIntensity} 
                                                        onChange={(e) => updateSelectedText('neonIntensity', Number(e.target.value))} 
                                                        className="flex-1 h-1 accent-click-cyan bg-gray-700 rounded appearance-none" 
                                                    />
                                                 </div>
                                            </div>
                                         )}
                                    </div>
                                </div>
                            </div>

                             {/* Section 5: Transform */}
                             <div className="bg-click-dark p-3 rounded-lg border border-click-border">
                                <div className="flex items-center justify-between mb-2">
                                     <label className="text-xs text-white">סיבוב</label>
                                     <button onClick={resetRotation} className="text-caption bg-click-border px-1 rounded hover:text-white" title="אפס סיבוב">
                                        <ICONS.RotateCcw className="w-3 h-3" />
                                     </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="range" 
                                        min="-180" max="180" 
                                        value={selectedText.rotation || 0}
                                        onChange={(e) => updateSelectedText('rotation', Number(e.target.value))}
                                        className="w-full accent-click-cyan h-2 bg-click-border rounded-lg appearance-none cursor-pointer"
                                    />
                                    <span className="text-caption w-8 text-right font-mono text-gray-400">{selectedText.rotation}°</span>
                                </div>
                             </div>

                             <div className="flex gap-3 mt-4">
                                <button onClick={duplicateText} className="flex-1 py-2 bg-click-border border border-click-border text-white hover:bg-white hover:text-black rounded transition-colors text-xs font-bold flex items-center justify-center gap-2">
                                    <ICONS.Copy className="w-4 h-4" /> שכפל
                                </button>
                                <button onClick={removeSelectedText} className="flex-1 py-2 bg-red-500/10 border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white rounded transition-colors text-xs font-bold flex items-center justify-center gap-2">
                                    <ICONS.Trash2 className="w-4 h-4" /> מחק
                                </button>
                             </div>

                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 pb-20">
                            <ICONS.Type className="w-12 h-12 mb-4 opacity-20" />
                            <p className="text-sm">בחר טקסט מהקנבס לעריכה</p>
                        </div>
                    )}
                </div>
            )}
        </div>

        {/* Edit Hook (mainTitle) Popover */}
        {editingConcept && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={closeEditHook}
          >
            <div
              className="bg-click-card border border-click-border rounded-xl shadow-xl max-w-md w-full p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <label className="text-caption text-gray-500 uppercase font-bold block mb-2">
                כותרת ראשית (הוק)
              </label>
              <input
                type="text"
                value={editMainTitleDraft}
                onChange={(e) => setEditMainTitleDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveEditHook()}
                className="w-full bg-click-dark border border-click-border focus:border-click-cyan text-white p-3 rounded text-sm font-bold mb-4"
                placeholder="1–3 מילים מושכות"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={closeEditHook}
                  className="px-4 py-2 rounded border border-click-border text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  ביטול
                </button>
                <button
                  type="button"
                  onClick={saveEditHook}
                  className="px-4 py-2 rounded bg-click-red text-white font-bold hover:bg-white hover:text-click-red transition-colors flex items-center gap-2"
                >
                  <ICONS.Save className="w-4 h-4" /> שמור
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Credits Modal */}
        <NotEnoughCreditsModal
          isOpen={showCreditsModal}
          onClose={() => setShowCreditsModal(false)}
          requiredCredits={creditsNeeded}
        />
    </div>
  );
};

export default Editor;